import { z } from 'zod';
import { sha256HexSchema } from './canonicalJson';
import { AUTHORING_REFERENCE_LEAD_US, authoringTimeSchema, interactionTagIdSchema } from './authoringPrimitives';
import { MACRO_PACKAGE_LIMITS } from './limits';

export const interactionTagSchema = z.object({ tagId: interactionTagIdSchema, eventSeq: authoringTimeSchema,
  eventUs: authoringTimeSchema, eventFrameSeq: authoringTimeSchema, referenceTargetUs: authoringTimeSchema,
  referenceFrameSeq: authoringTimeSchema, referenceActualUs: authoringTimeSchema,
}).strict().superRefine((tag, context) => {
  if (tag.referenceTargetUs !== tag.eventUs - AUTHORING_REFERENCE_LEAD_US || tag.referenceActualUs > tag.referenceTargetUs) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Reference must be an actual frame at or before input minus 100 ms' });
  }
  if (!tag.tagId.endsWith(`:${tag.eventSeq}`)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['tagId'], message: 'Tag identity must retain the input sequence' });
  }
});
export type InteractionTag = z.infer<typeof interactionTagSchema>;

export const referenceFrameAssetSchema = z.object({ frameSeq: authoringTimeSchema, tUs: authoringTimeSchema,
  file: z.string().regex(/^reference-frames\/(0|[1-9]\d*)\.png$/), sha256: sha256HexSchema,
  bytes: authoringTimeSchema.refine(bytes => bytes > 0), width: z.number().int().positive().max(16_384),
  height: z.number().int().positive().max(16_384), bitDepth: z.union([z.literal(8), z.literal(16)]),
}).strict().refine(frame => frame.file === `reference-frames/${frame.frameSeq}.png`, 'Frame path must match its native identity');
export type ReferenceFrameAsset = z.infer<typeof referenceFrameAssetSchema>;
export const referenceFrameCatalogSchema = z.object({ format: z.literal('playcast.macro.reference-frames'),
  formatVersion: z.literal(1), frames: z.array(referenceFrameAssetSchema).max(MACRO_PACKAGE_LIMITS.rawMaxFrames),
}).strict().superRefine((catalog, context) => {
  if (catalog.frames.some((frame, index) => index > 0 && (frame.frameSeq <= catalog.frames[index - 1]!.frameSeq
    || frame.tUs <= catalog.frames[index - 1]!.tUs))) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['frames'], message: 'Reference assets must have unique ordered frame identities and times' });
  }
});
export type ReferenceFrameCatalog = z.infer<typeof referenceFrameCatalogSchema>;
